<template>
  <div>
    <button
      type="button"
      class="base-select-nested-parent"
      :class="{
        active: isChecked || isPartiallyChecked,
        disabled: isDisabled
      }"
      :aria-label="isOpen ? $t('shared.selected-option') : $t('shared.option')"
      @click="handleClickOption(option)"
    >
      <div class="base-select-nested-parent__checkbox">
        <selected-icon
          v-if="isChecked"
          class="base-select-nested-parent__selected"
        />
        <partially-selected-icon
          v-else-if="isPartiallyChecked"
          class="base-select-nested-parent__selected"
        />
      </div>
      <p class="base-select-nested-parent__text">
        <span class="base-select-nested-parent__region-name">{{
          optionText
        }}</span>
        <template v-if="isOnlyOneOption">
          <span class="base-select-nested-parent__dealers-count">{{
            `(${
              option.sellers.length
                ? option.sellers.length
                : option.mostLocalSellers.length
            } ${$t("shared.dealers")})`
          }}</span>
          <chevron-image
            class="base-select-nested-parent__chevron"
            :class="{ rotate: isOpen }"
          ></chevron-image>
        </template>
      </p>
    </button>
    <div
      v-if="
        isOpen &&
          ((option.sellers && option.sellers.length > 1) ||
            (option.mostLocalSellers && option.mostLocalSellers.length > 1))
      "
      class="base-select-nested-parent__children"
    >
      <slot />
    </div>
  </div>
</template>

<script>
import { intersection, map } from "lodash";

import SelectedIcon from "@/assets/images/selected.svg";
import PartiallySelectedIcon from "@/assets/images/partially-selected.svg";
import ChevronImage from "@/assets/images/select-chevron.svg";

const CHECKBOX_STATE = {
  checked: "CHECKED",
  partiallyChecked: "PARTIALLY_CHECKED",
  notChecked: "NOT_CHECKED"
};

export default {
  name: "BaseSelectNestedParent",
  components: {
    SelectedIcon,
    PartiallySelectedIcon,
    ChevronImage
  },
  props: {
    option: {
      type: [Object, String, Number, Boolean],
      require: true
    },
    active: {
      type: [Array, String, Number, Boolean],
      default: false
    }
  },
  data() {
    return {
      isOpen: false
    };
  },
  computed: {
    isChecked() {
      return (
        this.getCheckboxState(this.option.sellers) === CHECKBOX_STATE.checked ||
        (this.option.sellers.length === 0 &&
          this.getCheckboxState(this.option.mostLocalSellers) ===
            CHECKBOX_STATE.checked)
      );
    },
    isPartiallyChecked() {
      return (
        this.getCheckboxState(this.option.sellers) ===
          CHECKBOX_STATE.partiallyChecked ||
        (this.option.sellers.length === 0 &&
          this.getCheckboxState(this.option.mostLocalSellers) ===
            CHECKBOX_STATE.partiallyChecked)
      );
    },
    optionText() {
      if (this.option.sellers.length === 1) {
        return `${this.option.name} - ${this.option.sellers[0].name}`;
      }

      if (
        this.option.sellers.length === 0 &&
        this.option.mostLocalSellers.length === 1
      ) {
        return `${this.option.name} - ${this.option.mostLocalSellers[0].name}`;
      }

      return `${this.option.name}`;
    },
    isDisabled() {
      return (
        (!this.option.sellers || this.option.sellers.length === 0) &&
        (!this.option.mostLocalSellers ||
          this.option.mostLocalSellers.length === 0)
      );
    },
    isOnlyOneOption() {
      return (
        (this.option.sellers && this.option.sellers.length > 1) ||
        (this.option.mostLocalSellers &&
          this.option.mostLocalSellers.length > 1)
      );
    }
  },
  methods: {
    handleClickOption(option) {
      if (this.option.sellers.length === 1) {
        return this.$emit("update-active-option", option.sellers[0]);
      }

      if (
        this.option.sellers.length === 0 &&
        this.option.mostLocalSellers.length === 1
      ) {
        return this.$emit("update-active-option", option.mostLocalSellers[0]);
      }

      if (!this.isOpen && this.option.sellers.length) {
        this.$emit("update-active-options", option.sellers);
      }

      if (
        !this.isOpen &&
        this.option.sellers.length === 0 &&
        this.option.mostLocalSellers.length
      ) {
        this.$emit("update-active-options", option.mostLocalSellers);
      }

      return (this.isOpen = !this.isOpen);
    },
    getCheckboxState(value) {
      const nestedOptionsPks = map(value, "pk");
      const nestedOptionsPksLength = nestedOptionsPks.length;
      const intersectionLength = intersection(this.active, nestedOptionsPks)
        .length;

      if (intersectionLength === 0) return CHECKBOX_STATE.notChecked;

      if (intersectionLength === nestedOptionsPksLength) {
        return CHECKBOX_STATE.checked;
      }

      return CHECKBOX_STATE.partiallyChecked;
    }
  }
};
</script>

<style nested lang="scss">
.base-select-nested-parent {
  width: 100%;
  display: flex;
  padding: 12px 0;
  color: $color-grey-400;

  &:hover {
    color: $color-black;
  }

  &.active {
    color: $color-black;
  }

  &.disabled {
    color: $color-grey-200;

    .base-select-nested-parent__checkbox {
      background-color: $color-grey-200;
      border-color: $color-grey-200;
    }
  }
}

.base-select-nested-parent__checkbox {
  @include sizing(10px);

  position: relative;
  overflow: hidden;
  display: inline-block;
  border: 1px solid $color-grey-100;
  margin-right: 9px;
  margin-top: 3px;
  flex-shrink: 0;

  .base-select-nested-parent__selected {
    position: absolute;
    left: -5px;
    top: -5px;
    transform: scale(.7);
  }
}

.base-select-nested-parent__children {
  padding-left: 15px;
}

.base-select-nested-parent__dealers-count {
  color: $color-grey-400;
  font-size: $font-size-m;
  margin-left: 5px;
}

.base-select-nested-parent__chevron {
  margin-left: 5px;
  fill: $color-grey-400;

  &.rotate {
    transform: rotate(180deg);
  }
}

.base-select-nested-parent__text {
  text-align: left;
  margin: 0;
}

.base-select-option__most-local-dealer {
  display: block;
}
</style>
